import React, { useState, useRef, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { supportedLanguages, detectBrowserLanguage } from '../data/languages';
import ELLaiLogo from '../components/ELLaiLogo';
import { api } from '../services/api';
import { PasswordMeter } from './ResetPasswordPage';
import './SignupPage.css';

function SignupPage({ onLogin }) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Check for invite token and pre-filled email in URL
  const inviteToken = searchParams.get('invite');
  const inviteEmail = searchParams.get('email');

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: inviteEmail || '', // Pre-fill email from invitation
    password: '',
    confirmPassword: '',
    birthDate: '',
    language: detectBrowserLanguage(), // Auto-detect browser language
  });

  // Store invite token for after signup
  useEffect(() => {
    if (inviteToken) {
      sessionStorage.setItem('pendingInvite', inviteToken);
    }
  }, [inviteToken]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [acknowledgedDisclaimer, setAcknowledgedDisclaimer] = useState(false);
  const [disclaimerScrolledToBottom, setDisclaimerScrolledToBottom] = useState(false);
  const [termsScrolledToBottom, setTermsScrolledToBottom] = useState(false);

  const disclaimerRef = useRef(null);
  const termsRef = useRef(null);

  const handleDisclaimerScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    // Check if scrolled to bottom (with small buffer for rounding)
    if (scrollTop + clientHeight >= scrollHeight - 10) {
      setDisclaimerScrolledToBottom(true);
    }
  };

  const handleTermsScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (scrollTop + clientHeight >= scrollHeight - 10) {
      setTermsScrolledToBottom(true);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!agreedToTerms) {
      setError('You must agree to the Terms and Conditions to continue');
      return;
    }

    if (!acknowledgedDisclaimer) {
      setError('You must acknowledge the disclaimer to continue');
      return;
    }

    // Age verification - must be 13 or older
    if (formData.birthDate) {
      const today = new Date();
      const birth = new Date(formData.birthDate);
      let age = today.getFullYear() - birth.getFullYear();
      const monthDiff = today.getMonth() - birth.getMonth();
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
        age--;
      }
      if (age < 13) {
        setError('You must be at least 13 years old to create an account. This is required by federal law (COPPA).');
        return;
      }
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    if (!/[a-zA-Z]/.test(formData.password) || !/[0-9]/.test(formData.password)) {
      setError('Password must contain at least one letter and one number');
      return;
    }

    setLoading(true);

    const fullName = `${formData.firstName} ${formData.lastName}`.trim();

    try {
      const response = await api.post('/api/auth/signup', {
        fullName: fullName,
        email: formData.email,
        password: formData.password,
        role: 'creator', // Default role
        birthDate: formData.birthDate || null,
        language: formData.language,
      });

      const data = await response.json();

      if (response.ok) {
        onLogin(data.token, data.user);

        // Check for pending invitation and redirect
        const pendingInvite = sessionStorage.getItem('pendingInvite');
        if (pendingInvite) {
          sessionStorage.removeItem('pendingInvite');
          navigate(`/invite/${pendingInvite}`, { replace: true });
        } else if (sessionStorage.getItem('pendingTier')) {
          // They clicked a pricing CTA before having an account —
          // resume that purchase instead of dropping them on the dashboard
          navigate('/pricing', { replace: true });
        }
      } else {
        setError(data.error || 'Signup failed');
      }
    } catch (err) {
      setError('Unable to connect to server');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="signup-page">
      <div className="signup-container">
        <div className="signup-content">
          {/* Logo / Brand */}
          <div className="brand">
            <ELLaiLogo size="medium" />
          </div>

          {/* Signup Form */}
          <div className="card signup-card">
            <h2>Create your account</h2>
            <p className="subtitle mb-lg">Start building your digital ELLai today</p>

            {error && (
              <div className="error-message">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="firstName">First Name</label>
                  <input
                    id="firstName"
                    name="firstName"
                    type="text"
                    value={formData.firstName}
                    onChange={handleChange}
                    placeholder="John"
                    required
                    autoFocus
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="lastName">Last Name</label>
                  <input
                    id="lastName"
                    name="lastName"
                    type="text"
                    value={formData.lastName}
                    onChange={handleChange}
                    placeholder="Smith"
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="email">Email</label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="you@example.com"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="birthDate">Birthday</label>
                <input
                  id="birthDate"
                  name="birthDate"
                  type="date"
                  value={formData.birthDate}
                  onChange={handleChange}
                  required
                />
                <small style={{ color: '#666', marginTop: '0.25rem', display: 'block' }}>
                  You must be at least 13 years old to use ELLai
                </small>
              </div>

              <div className="form-group">
                <label htmlFor="language">Preferred Language</label>
                <select
                  id="language"
                  name="language"
                  value={formData.language}
                  onChange={handleChange}
                  className="language-select"
                >
                  {supportedLanguages.map((lang) => (
                    <option key={lang.code} value={lang.code}>
                      {lang.flag} {lang.name} ({lang.nativeName})
                    </option>
                  ))}
                </select>
                <small style={{ color: '#666', marginTop: '0.25rem', display: 'block' }}>
                  You can change this later in settings
                </small>
              </div>

              <div className="form-group">
                <label htmlFor="password">Password</label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="8+ characters with a letter and a number"
                  required
                />
                <PasswordMeter password={formData.password} />
              </div>

              <div className="form-group">
                <label htmlFor="confirmPassword">Confirm Password</label>
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  placeholder="Re-enter your password"
                  required
                />
                {formData.confirmPassword && formData.confirmPassword !== formData.password && (
                  <small style={{ color: '#d9534f', marginTop: '0.25rem', display: 'block' }}>
                    Passwords don't match
                  </small>
                )}
              </div>

              {/* ELLai Disclaimer - Shown First */}
              <div className="agreement-section">
                <h3 className="agreement-title">ELLai Disclaimer</h3>
                <div
                  className="scrollable-agreement"
                  ref={disclaimerRef}
                  onScroll={handleDisclaimerScroll}
                >
                  <p>
                    <strong>ELLai</strong> is a memory-preservation technology designed to help people reflect, remember,
                    and feel connected to the stories, values, and voice of someone they love.
                  </p>
                  <p>
                    ELLai is <strong>not religiously affiliated</strong> and is not intended to represent spiritual
                    communication, consciousness, or an afterlife. The responses generated are based solely on information
                    intentionally provided during life and are meant to support remembrance, comfort, and reflection in a
                    positive and respectful way.
                  </p>
                  <p>
                    We honor and respect all belief systems and encourage users to engage with ELLai in a way that aligns
                    with their personal values and worldview.
                  </p>
                </div>
                {!disclaimerScrolledToBottom && (
                  <div className="scroll-indicator">
                    ↓ Scroll to read all
                  </div>
                )}
                <label className={`checkbox-label inline-checkbox ${!disclaimerScrolledToBottom ? 'disabled' : ''}`}>
                  <input
                    type="checkbox"
                    checked={acknowledgedDisclaimer}
                    onChange={(e) => setAcknowledgedDisclaimer(e.target.checked)}
                    disabled={!disclaimerScrolledToBottom}
                    required
                  />
                  <span>I acknowledge and understand this disclaimer</span>
                </label>
              </div>

              {/* Terms and Conditions */}
              <div className="agreement-section">
                <h3 className="agreement-title">Terms and Conditions</h3>
                <div
                  className="scrollable-agreement"
                  ref={termsRef}
                  onScroll={handleTermsScroll}
                >
                  <h4>1. Acceptance of Terms</h4>
                  <p>
                    By accessing and using ELLai ("the Service"), you accept and agree to be bound by the terms
                    and provisions of this agreement. If you do not agree to these terms, please do not use the Service.
                    These Terms constitute a legally binding agreement between you and ELLai.
                  </p>

                  <h4>2. Description of Service</h4>
                  <p>
                    ELLai provides a platform for creating AI-powered digital replicas of individuals. The Service
                    allows users to preserve voices, memories, and personality traits for future generations through
                    artificial intelligence technology. The Service is designed for personal memorial and remembrance
                    purposes only.
                  </p>

                  <h4>3. Voice Cloning and Digital Replica Consent</h4>
                  <p>
                    <strong>You represent and warrant that:</strong>
                  </p>
                  <ul>
                    <li>You are creating a digital replica of YOURSELF only, or you have obtained explicit, written consent from the individual whose voice and likeness you are replicating</li>
                    <li>If creating a replica of a deceased individual, you are the legal heir, executor, or have obtained written authorization from the estate or rights holder as required by applicable state and federal law, including but not limited to California Civil Code Section 3344.1 and New York Civil Rights Law</li>
                    <li>You understand that unauthorized voice cloning or digital replica creation may violate state and federal laws regarding right of publicity, privacy, and identity theft, with penalties up to $10,000 or more per violation</li>
                    <li>You will not use the Service to impersonate any person for fraudulent, deceptive, or harmful purposes</li>
                    <li>You acknowledge that all AI-generated content can be traced back to your account</li>
                  </ul>

                  <h4>4. User Responsibilities and Prohibited Uses</h4>
                  <p>You agree to:</p>
                  <ul>
                    <li>Provide accurate and truthful information during the onboarding process</li>
                    <li>Maintain the security of your account credentials</li>
                    <li>Use the Service in accordance with all applicable laws and regulations</li>
                    <li>Respect the privacy and consent of all individuals involved</li>
                    <li>Not use the Service for any illegal, harmful, fraudulent, defamatory, or harassing purposes</li>
                    <li>Not use the Service to generate content that violates any third party's intellectual property, privacy, or publicity rights</li>
                    <li>Not use the Service for commercial purposes without prior written consent</li>
                    <li>Not reverse engineer, decompile, or attempt to extract the source code of the Service</li>
                  </ul>

                  <h4>5. Content and Data Ownership</h4>
                  <p>
                    You retain ownership of all original content you provide to ELLai. By using the Service, you grant ELLai
                    a limited, non-exclusive license to process, store, and use your content solely for the purpose of providing
                    the Service to you. This license terminates when you delete your account. We implement industry-standard
                    security measures to protect your data. ELLai does not claim ownership of your voice data, likeness,
                    or the AI models created from your content.
                  </p>

                  <h4>6. Privacy and Confidentiality</h4>
                  <p>
                    Your privacy is important to us. All personal information and conversations are encrypted and stored
                    securely. We will never sell your data to third parties. We may share data only: (a) with your explicit
                    consent, (b) as required by law or valid legal process, (c) to protect our rights or safety, or
                    (d) with service providers who assist in operating the Service under strict confidentiality agreements.
                  </p>

                  <h4>6A. Data Security and Biometric Information</h4>
                  <p>
                    <strong>Security Measures:</strong> ELLai implements industry-standard security measures including
                    encryption at rest and in transit, access controls, and regular security audits. However, no system
                    is completely secure, and we cannot guarantee absolute security of your data.
                  </p>
                  <p>
                    <strong>Biometric Data Notice:</strong> Your voice recordings and voice prints may constitute biometric
                    data under certain state laws including the Illinois Biometric Information Privacy Act (BIPA). By using
                    the Service, you acknowledge and consent to the collection, storage, and processing of your voice data
                    for the purpose of creating and operating your digital replica. Voice data will be retained until you
                    delete your account or for three (3) years after your last interaction with the Service, whichever
                    comes first.
                  </p>
                  <p>
                    <strong>Data Breach Notification:</strong> In the event of a security breach that affects your personal
                    or biometric data, ELLai will notify you in accordance with applicable state and federal data breach
                    notification laws. Such notification will be provided within the timeframes required by law (typically
                    30-60 days depending on jurisdiction).
                  </p>
                  <p>
                    <strong>Assumption of Risk:</strong> YOU ACKNOWLEDGE THAT THE TRANSMISSION AND STORAGE OF DATA OVER
                    THE INTERNET INVOLVES INHERENT RISKS. YOU ASSUME ALL RISKS ASSOCIATED WITH THE USE OF THE SERVICE,
                    INCLUDING ANY UNAUTHORIZED ACCESS TO OR USE OF YOUR DATA RESULTING FROM THIRD-PARTY CRIMINAL ACTIVITY
                    OR SECURITY BREACHES BEYOND ELLAI'S REASONABLE CONTROL. THIS ASSUMPTION OF RISK IS A MATERIAL TERM
                    OF THIS AGREEMENT.
                  </p>

                  <h4>7. AI-Generated Content Disclaimer</h4>
                  <p>
                    Responses generated by your replica are based on the information you provide and are created using
                    artificial intelligence. <strong>YOU ACKNOWLEDGE AND AGREE THAT:</strong>
                  </p>
                  <ul>
                    <li>AI-generated content may not perfectly represent the original person's actual thoughts, opinions, beliefs, or intentions</li>
                    <li>The replica is a technological simulation, not a continuation of consciousness or spiritual presence</li>
                    <li>AI outputs may occasionally be inaccurate, incomplete, or unexpected</li>
                    <li>ELLai does not guarantee the accuracy, completeness, or reliability of AI-generated responses</li>
                    <li>Users should not rely on AI-generated content for medical, legal, financial, or other professional advice</li>
                  </ul>

                  <h4>8. Mental Health and Emotional Wellbeing Disclaimer</h4>
                  <p>
                    <strong>IMPORTANT:</strong> The Service is not a substitute for professional mental health care, grief
                    counseling, or therapy. If you are experiencing emotional distress, depression, or grief-related
                    difficulties, please seek help from a qualified mental health professional. The Service is intended
                    to support positive remembrance and should not be used as the sole means of processing grief or loss.
                  </p>

                  <h4>9. Access Control</h4>
                  <p>
                    You maintain full control over who can access your replica. You can grant, modify, or revoke access
                    permissions at any time. You are responsible for ensuring that anyone you grant access to complies
                    with these Terms. Family members and other authorized users agree to use the Service respectfully
                    and in accordance with your content preferences.
                  </p>

                  <h4>10. Disclaimer of Warranties</h4>
                  <p>
                    THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS
                    OR IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
                    PARTICULAR PURPOSE, TITLE, AND NON-INFRINGEMENT. ELLAI DOES NOT WARRANT THAT THE SERVICE WILL
                    BE UNINTERRUPTED, ERROR-FREE, SECURE, OR FREE OF VIRUSES OR OTHER HARMFUL COMPONENTS.
                  </p>

                  <h4>11. Limitation of Liability</h4>
                  <p>
                    TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT SHALL ELLAI, ITS AFFILIATES,
                    DIRECTORS, EMPLOYEES, AGENTS, OR LICENSORS BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL,
                    CONSEQUENTIAL, PUNITIVE, OR EXEMPLARY DAMAGES, INCLUDING BUT NOT LIMITED TO DAMAGES FOR LOSS
                    OF PROFITS, GOODWILL, USE, DATA, OR OTHER INTANGIBLE LOSSES, REGARDLESS OF WHETHER ELLAI HAS
                    BEEN ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.
                  </p>
                  <p>
                    <strong>DATA BREACH LIMITATION:</strong> WITHOUT LIMITING THE FOREGOING, ELLAI SHALL NOT BE LIABLE
                    FOR ANY DAMAGES ARISING FROM UNAUTHORIZED ACCESS TO OR THEFT OF YOUR DATA, INCLUDING VOICE DATA,
                    BIOMETRIC INFORMATION, OR DIGITAL REPLICA DATA, WHERE SUCH ACCESS RESULTS FROM: (A) SOPHISTICATED
                    CYBER ATTACKS BEYOND INDUSTRY-STANDARD SECURITY MEASURES; (B) YOUR FAILURE TO MAINTAIN SECURE
                    CREDENTIALS; (C) THIRD-PARTY CRIMINAL ACTIVITY; OR (D) CIRCUMSTANCES BEYOND ELLAI'S REASONABLE CONTROL.
                  </p>
                  <p>
                    ELLAI'S TOTAL LIABILITY FOR ALL CLAIMS ARISING FROM OR RELATED TO THE SERVICE, INCLUDING CLAIMS
                    RELATED TO DATA SECURITY OR PRIVACY, SHALL NOT EXCEED THE GREATER OF (A) THE AMOUNTS PAID BY YOU TO
                    ELLAI IN THE TWELVE (12) MONTHS PRIOR TO THE CLAIM OR (B) ONE HUNDRED DOLLARS ($100). THIS LIMITATION
                    APPLIES TO ALL THEORIES OF LIABILITY, WHETHER BASED ON CONTRACT, TORT, NEGLIGENCE, STRICT LIABILITY,
                    OR OTHERWISE.
                  </p>

                  <h4>12. Indemnification and Hold Harmless</h4>
                  <p>
                    You agree to indemnify, defend, and hold harmless ELLai, its affiliates, officers, directors,
                    employees, agents, and licensors from and against any and all claims, damages, losses, liabilities,
                    costs, and expenses (including reasonable attorneys' fees) arising out of or related to: (a) your
                    use of the Service; (b) your violation of these Terms; (c) your violation of any rights of another,
                    including intellectual property, privacy, or publicity rights; (d) any content you provide to the
                    Service; (e) your creation of a digital replica without proper consent or authorization; or
                    (f) any claim that your use of the Service caused damage to a third party.
                  </p>

                  <h4>13. Dispute Resolution and Arbitration</h4>
                  <p>
                    <strong>PLEASE READ THIS SECTION CAREFULLY. IT AFFECTS YOUR LEGAL RIGHTS.</strong>
                  </p>
                  <p>
                    Any dispute, controversy, or claim arising out of or relating to these Terms or the Service shall
                    be resolved by binding arbitration administered by the American Arbitration Association ("AAA")
                    in accordance with its Consumer Arbitration Rules. The arbitration shall be conducted in English,
                    and the arbitrator's decision shall be final and binding. Judgment on the award may be entered
                    in any court of competent jurisdiction.
                  </p>
                  <p>
                    <strong>CLASS ACTION WAIVER:</strong> YOU AGREE THAT ANY ARBITRATION OR PROCEEDING SHALL BE
                    LIMITED TO THE DISPUTE BETWEEN ELLAI AND YOU INDIVIDUALLY. YOU WAIVE ANY RIGHT TO PARTICIPATE
                    IN A CLASS ACTION LAWSUIT OR CLASS-WIDE ARBITRATION.
                  </p>
                  <p>
                    <strong>Exceptions:</strong> Either party may bring claims in small claims court if eligible.
                    Either party may seek injunctive relief in any court of competent jurisdiction for infringement
                    of intellectual property rights.
                  </p>

                  <h4>14. Governing Law</h4>
                  <p>
                    These Terms shall be governed by and construed in accordance with the laws of the State of Delaware,
                    without regard to its conflict of law provisions. For any matters not subject to arbitration, you
                    consent to the exclusive jurisdiction of the state and federal courts located in Delaware.
                  </p>

                  <h4>15. Termination</h4>
                  <p>
                    You may delete your account and all associated data at any time through your account settings.
                    ELLai reserves the right to suspend or terminate your account immediately, without prior notice
                    or liability, for any reason, including if you breach these Terms. Upon termination, your right
                    to use the Service will immediately cease. Sections 10-14 shall survive any termination.
                  </p>

                  <h4>16. Changes to Terms</h4>
                  <p>
                    We may update these Terms from time to time. We will notify you of material changes by posting
                    the new Terms on this page and updating the "Last Updated" date. Your continued use of the Service
                    after such changes constitutes acceptance of the new Terms. If you do not agree to the modified
                    Terms, you must stop using the Service and delete your account.
                  </p>

                  <h4>17. Severability</h4>
                  <p>
                    If any provision of these Terms is found to be unenforceable or invalid, that provision shall be
                    limited or eliminated to the minimum extent necessary so that these Terms shall otherwise remain
                    in full force and effect.
                  </p>

                  <h4>18. Entire Agreement</h4>
                  <p>
                    These Terms, together with the Privacy Policy and any other agreements expressly incorporated by
                    reference, constitute the entire agreement between you and ELLai concerning the Service and
                    supersede all prior agreements and understandings.
                  </p>

                  <h4>19. Contact Information</h4>
                  <p>
                    For questions about these Terms and Conditions, please contact us through the Help feature in
                    your dashboard or at legal@ellai.app.
                  </p>

                  <p className="terms-date">
                    <strong>Last Updated:</strong> January 19, 2026
                  </p>
                </div>
                {!termsScrolledToBottom && (
                  <div className="scroll-indicator">
                    ↓ Scroll to read all
                  </div>
                )}
                <label className={`checkbox-label inline-checkbox ${!termsScrolledToBottom ? 'disabled' : ''}`}>
                  <input
                    type="checkbox"
                    checked={agreedToTerms}
                    onChange={(e) => setAgreedToTerms(e.target.checked)}
                    disabled={!termsScrolledToBottom}
                    required
                  />
                  <span>I agree to the Terms and Conditions and <Link to="/privacy" className="link" target="_blank">Privacy Policy</Link></span>
                </label>
              </div>


              <button
                type="submit"
                className="btn btn-primary btn-full"
                disabled={loading || !agreedToTerms || !acknowledgedDisclaimer}
              >
                {loading ? 'Creating account...' : 'Create account'}
              </button>
            </form>

            <div className="divider">
              <span>or</span>
            </div>

            <p className="login-prompt">
              Already have an account?{' '}
              <Link to="/login" className="link">
                Sign in here
              </Link>
            </p>
          </div>

          {/* Footer */}
          <p className="footer-text">
            By creating an account, you're taking the first step in preserving 
            your voice and memories for the people who matter most.
          </p>
        </div>
      </div>
    </div>
  );
}

export default SignupPage;
